import { Trans, useTranslation } from "react-i18next";
import { usePictureSets } from "../../hooks/usePictureSets";
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import {
  PhotographerOrSubjectListWrapper,
  SubjectListItem,
  SubjectYearListHeader,
} from "../../Components/Checkout/SubjectSelector";
import makeStyles from "@mui/styles/makeStyles";
import { Skeleton } from "@mui/material";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Popup, {
  PopupContent,
  PopupHeading,
} from "../../../components/Common/Popup";
import AddSubjectDialog from "./AddSubjectDialog";

const useStyles = makeStyles((theme) => ({
  selectButtonContainer: {
    borderColor: theme.palette.primary.main,
    borderWidth: "2px",
    padding: 0,
    "&:hover": {
      borderWidth: "2px",
    },
    "& > legend": {
      fontSize: "10px",
      marginLeft: "auto",
      marginRight: "auto",
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      letterSpacing: "1.1px",
    },
    "& button": {
      display: "flex",
      justifyContent: "space-between",
    },
    "& button:hover": {
      border: "none",
      backgroundColor: "transparent",
    },
  },
  subjectNameAndGroup: {
    fontSize: "15px",
    textAlign: "center",
    color: theme.palette.primary.main,
    fontWeight: "normal",
    textTransform: "none",
    lineHeight: "1.10em",
  },
  groupAndJob: {
    fontSize: "12px",
    fontStyle: "italic",
  },
  expandMore: {
    marginLeft: theme.spacing(2),
    width: "40px",
  },
  myPictureSetsAvatar: {
    marginRight: theme.spacing(2),
    width: "39px",
    height: "39px",
    // Primary color with some transparancey
    border: "1px solid " + theme.palette.primary.main + "61",
    backgroundPosition: "center top",
    backgroundSize: "cover",
    borderRadius: "50%",
  },
}));

const SubjectSelector = ({ pictureSets, legend, disableGutters }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { currentPictureSet, pictureSets: _pictureSets } = usePictureSets();
  const [showSubjectSelector, setShowSubjectSelector] = useState(false);
  const [showAddPictureSetDialog, setShowAddPictureSetDialog] =
    useState(false);

  const handleAddPictureSet = () => {
    setShowAddPictureSetDialog(true);
    setShowSubjectSelector(false);
  };

  if (!pictureSets) {
    pictureSets = _pictureSets;
  }

  if (!pictureSets) {
    return null;
  }

  let legendText = legend;
  if (!legendText) {
    legendText =
      pictureSets?.length > 1 ? t("My picture sets") : t("My picture set");
  }

  return (
    <Box mb={2}>
      <Container maxWidth="xs" disableGutters={disableGutters}>
        <fieldset
          className={`MuiButton-root MuiButton-outlinedPrimary ${classes.selectButtonContainer}`}
        >
          <legend>{legendText}</legend>
          <SubjectSelectorButton
            pictureSets={pictureSets}
            currentPictureSet={currentPictureSet}
            setShowSubjectSelector={setShowSubjectSelector}
          />
        </fieldset>
      </Container>
      {showAddPictureSetDialog && (
        <AddSubjectDialog onClose={() => setShowAddPictureSetDialog(false)} />
      )}
      {showSubjectSelector && (
        <SelectSubjectDialog
          onClose={() => setShowSubjectSelector(false)}
          pictureSets={pictureSets}
          handleAddPictureSet={handleAddPictureSet}
        />
      )}
    </Box>
  );
};

const SubjectSelectorButton = ({
  pictureSets,
  currentPictureSet,
  setShowSubjectSelector,
}) => {
  const classes = useStyles();
  return (
    <Button
      variant="outlined"
      color="primary"
      fullWidth
      sx={{
        display: "flex",
        justifyContent: "center",
        marginLeft: "auto",
        marginRight: "auto",
        border: "none",
        padding: 0,
        position: "relative",
        top: "-3px",
        "&:active": {
          border: "none",
        },
      }}
      onClick={() => setShowSubjectSelector(true)}
    >
      <div
        className={classes.myPictureSetsAvatar}
        style={{ backgroundImage: `url(${currentPictureSet.avatar.url})` }}
      ></div>
      <div className={classes.subjectNameAndGroup}>
        <div className={classes.subjectName}>
          {currentPictureSet.subject.name}
        </div>
        <div className={classes.groupAndJob}>
          {currentPictureSet.subject.group}
        </div>
      </div>
      <div className={classes.expandMore}>
        {pictureSets.length > 1 && setShowSubjectSelector && (
          <Icon color="primary">expand_more</Icon>
        )}
      </div>
    </Button>
  );
};

export const SubjectSelectorSkeleton = () => {
  const classes = useStyles();
  return (
    <Box mb={2}>
      <div>
        <button className={classes.selectSubjectButton}>
          <div className={classes.subjectNameAndGroup}>
            <div className={classes.subjectName}>
              <Skeleton variant="text" width={150} />
            </div>
            <div className={classes.groupAndJob}>
              <Skeleton variant="text" width={150} />
            </div>
          </div>
        </button>
      </div>
    </Box>
  );
};

export const SelectSubjectDialog = ({
  onClose,
  pictureSets,
  handleAddPictureSet,
}) => {
  const { t } = useTranslation();
  const { currentPictureSet, setCurrentPictureSet } = usePictureSets();

  const handleSubjectChange = (pictureSet) => {
    setCurrentPictureSet(pictureSet);
    onClose();
  };

  return (
    <Popup fullWidth="md" maxWidth="xs" onClose={onClose}>
      <PopupHeading>
        <Trans>My picture sets</Trans>
      </PopupHeading>
      <PopupContent>
        <PhotographerOrSubjectListWrapper
          currentImageSet={currentPictureSet}
          imageSets={pictureSets}
          inDropDown={false}
          onChangeImageSet={handleSubjectChange}
        />
        <SubjectYearListHeader header={t("Add pictures")} />
        {handleAddPictureSet && (
          <SubjectListItem
            disabled={false}
            primaryLabel={t("Add picture set")}
            icon="add"
            onChangeImageSet={handleAddPictureSet}
            selectable={true}
            isSelected={false}
          />
        )}
      </PopupContent>
    </Popup>
  );
};

export default SubjectSelector;
